import React from 'react';

import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import Button from '@asteria/component-core/button';
import Wrapper, {
	Content,
	Footer,
	FooterSection,
	Header,
} from '@asteria/component-core/wrapper';

import List from '@asteria/component-list';

import { useTranslationFn } from '@asteria/language/hooks';
import { cn } from '@asteria/utils-funcs/classes';

import Feedback from './feedback';
import { useLinkedValues, useSections } from './hooks';
import Section from './section';
import Splash from './splash';

import './styles.scss';

/**
 * @typedef { import('./types').Props } Props
 * @typedef { import('./types').ChildrenProps } ChildrenProps
 */

/** @type { React.FC<Props> } */
const TransactionDetails = React.memo(function TransactionDetails(props) {
	const { className, onClose, onSubmit, id } = props;

	const translate = useTranslationFn();

	const query = useQuery({
		queryKey: ['transactions', 'details', id],
		queryFn: async () => onSubmit?.('transactions:details', { id }),

		placeholderData: null,

		refetchOnMount: true,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
	});

	const links = useLinkedValues(query);
	const sections = useSections(query);

	const next = { ...props, query, links };

	return (
		<Wrapper
			className={cn('asteria-component__transaction-details', className)}
			scroll
		>
			<Header onClose={onClose}>
				{translate('transaction.details.sidebar.header', {
					data: {
						item: query?.data,
						...links,
						bank: links.bankTransaction,
					},
				})}
			</Header>
			<Content className="gap-4">
				<Splash {...next} />
				<List>
					{sections.map((section, index) => (
						<Section
							key={section.type}
							{...next}
							section={section}
							first={index === 0}
						/>
					))}
				</List>
				<Feedback {...next} />
			</Content>
			<Footer>
				<FooterSection position="last">
					<Button
						variant="primary"
						label={translate(['button.close', 'action.close'], {
							data: {
								item: query?.data,
								...links,
								bank: links.bankTransaction,
							},
						})}
						onClick={onClose}
					/>
				</FooterSection>
			</Footer>
		</Wrapper>
	);
});

TransactionDetails.displayName = 'TransactionDetails';

TransactionDetails.propTypes = {
	className: PropTypes.string,
	onClose: PropTypes.func,
	onAction: PropTypes.func,
	onSubmit: PropTypes.func,
	id: PropTypes.string,
};

export default TransactionDetails;
